<template>
  <div class="cart__item">
    <div class="cart__item-image">
      <sib-link
        v-if="item"
        @click="$emit('link-clicked')"
        :to="
          buildProductHandle(
            `/products/${item.merchandise?.product?.handle || 'all'}?variant=${
              item.merchandise?.id
            }`
          )
        "
        :title="item.merchandise.product.title || ''"
      >
        <sib-image
          :class="{ overlay: isPreviewCustomilyLoading }"
          :alt="item.merchandise.product.title"
          :width="1024"
          :height="1024"
          :src="
            !isUseProductImageToPreviewCustomily
              ? variantImage
              : getPreviewImage
          "
          @error="onPreviewImageError"
          loading="lazy"
        >
        </sib-image>
      </sib-link>
      <sib-skeleton-loading v-else size="square"></sib-skeleton-loading>
      <div
        v-if="isPreviewCustomilyLoading"
        class="product-preview-customily-loading lds-ring"
      >
        <div></div>
        <div></div>
        <div></div>
        <div></div>
      </div>
    </div>
    <div
      v-if="item"
      class="cart__item-image-preview link"
      @click="previewImage"
    >
      <ZoominIcon />
      Preview
    </div>
    <div class="cart__item-content">
      <div class="cart__item-title">
        <sib-link
          v-if="item"
          :to="
            buildProductHandle(
              `/products/${
                item.merchandise?.product?.handle || 'all'
              }?variant=${item.merchandise?.id}`
            )
          "
          :title="productTitleBaseOnABTest ? productTitleBaseOnABTest : (item.merchandise.product.title || '')"
          @click="$emit('link-clicked')"
        >
          {{ productTitleBaseOnABTest ? productTitleBaseOnABTest : item.merchandise.product.title }}
        </sib-link>
        <template v-else>
          <sib-skeleton-loading :count="2"></sib-skeleton-loading>
        </template>
      </div>
      <div class="cart__item-price">
        <template v-if="item">
          <span class="cart__item-price-final">
            {{ formatPrice(finalPrice.price) }}
          </span>
          <span
            class="cart__item-price-compare"
            v-if="
              finalPrice.compareAtPrice && parseFloat(finalPrice.compareAtPrice)
            "
          >
            {{ formatPrice(finalPrice.compareAtPrice) }}
          </span>
        </template>
        <template v-else>
          <sib-skeleton-loading width="5em"></sib-skeleton-loading>
        </template>
      </div>

      <div class="cart__item-options">
        <template v-if="item">
          {{
            item.merchandise.selectedOptions
              ?.map((option) => option.value)
              .join(' / ')
          }}
        </template>
        <template v-else>
          <sib-skeleton-loading width="8em"></sib-skeleton-loading>
        </template>
      </div>
      <div
        v-if="item"
        class="cart__item-image-preview link"
        @click="previewImage"
      >
        <ZoominIcon />
        Preview
      </div>
      <div
        class="cart__item-same-design"
        v-if="item && getUpsellSameDesignProductTitleWith"
      >
        The design of this product will be the same as the product titled '{{
          getUpsellSameDesignProductTitleWith
        }}'
      </div>

      <div
        v-if="item && !isMademineProduct"
        class="cart__item-attributes hidden"
        v-show="filteredAttributes.length"
      >
        <details>
          <summary @click="onTogglePersonalization">Personalized</summary>
          <div class="cart__item-attribute-list">
            <template
              v-for="(attribute, index) of filteredAttributes"
              :key="index"
            >
              <span class="cart__item-attribute-title">
                {{ attribute.key }}
              </span>
              <span class="cart__item-attribute-value">
                {{ attribute.value }}
                <span
                  v-if="hasCustomImageLink(attribute.key)"
                  @click="previewCustomImage(attribute.key)"
                  :class="{
                    'font-weight-normal link text-primary': hasCustomImageLink(
                      attribute.key
                    ),
                  }"
                >
                  Link
                </span>
                <template v-if="hasCustomColor(attribute.key)">
                  <div
                    v-for="(color, i) in getCustomColor(attribute.key).split(
                      ','
                    )"
                    :key="i"
                    class="custom-color-picker"
                    :style="`background:${color}`"
                  ></div>
                </template>
              </span>
            </template>
          </div>
        </details>
      </div>
      <div
        class="cart__item-attributes hidden"
        v-else-if="
          item && isMademineProduct && customTextsMademineProduct.length
        "
      >
        <details>
          <summary @click="onTogglePersonalization">Personalized</summary>
          <div class="cart__item-attribute-list">
            <template
              v-for="(attribute, index) of filteredAttributes"
              :key="index"
            >
              <template v-if="attribute.key == MADEMINE_PREVIEW_ATTRIBUTE_KEY">
                <template
                  v-for="(text, i) in customTextsMademineProduct"
                  :key="i"
                >
                  <span class="cart__item-attribute-title">
                    Text
                    {{
                      `${customTextsMademineProduct.length > 1 ? i + 1 : ''}`
                    }}
                  </span>
                  <span class="cart__item-attribute-value">
                    {{ text }}
                  </span>
                </template>
              </template>
            </template>
          </div>
        </details>
      </div>

      <sib-link
        @click="onClickEditPersonalization"
        :to="customilyEditPersonalizationLink"
        v-if="item && customilyEditPersonalizationLink"
        class="cart__item-edit-persionalization hidden"
        :title="item.title || ''"
      >
        Edit personalization
      </sib-link>

      <div class="cart__item-quantity-container">
        <template v-if="item">
          <div class="cart__item-quantity">
            <Quantity
              @update="$emit('update-quantity', $event, item?.id)"
              base-class="cart__item"
              :quantity="item.quantity"
            />
          </div>
          <div class="cart__item-delete-button">
            <TrashIcon class="btn-icon" @click="$emit('delete-item', item?.id)" />
          </div>
          <div
            class="cart__item-edit-persionalization"
            v-if="item && customilyEditPersonalizationLink"
            @click="onClickEditPersonalization()"
          >
            <svg class="btn-icon" width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><path fill="#000000" d="M441 58.9L453.1 71c9.4 9.4 9.4 24.6 0 33.9L424 134.1L377.9 88L407 58.9c9.4-9.4 24.6-9.4 33.9 0zM209.8 256.2L344 121.9l46.1 46.1l-134.3 134.2c-2.9 2.9-6.5 5-10.4 6.1L186.9 325l16.7-58.5c1.1-3.9 3.2-7.5 6.1-10.4zM373.1 25L175.8 222.2c-8.7 8.7-15 19.4-18.3 31.1l-28.6 100c-2.4 8.4-.1 17.4 6.1 23.6s15.2 8.5 23.6 6.1l100-28.6c11.8-3.4 22.5-9.7 31.1-18.3L487 138.9c28.1-28.1 28.1-73.7 0-101.8L474.9 25c-28.1-28.1-73.7-28.1-101.8 0M88 64c-48.6 0-88 39.4-88 88v272c0 48.6 39.4 88 88 88h272c48.6 0 88-39.4 88-88V312c0-13.3-10.7-24-24-24s-24 10.7-24 24v112c0 22.1-17.9 40-40 40H88c-22.1 0-40-17.9-40-40V152c0-22.1 17.9-40 40-40h112c13.3 0 24-10.7 24-24s-10.7-24-24-24z"/></svg>
          </div>
        </template>
        <template v-else>
          <sib-skeleton-loading width="6em" height="2em"></sib-skeleton-loading>
          <sib-skeleton-loading width="3em" height="2em"></sib-skeleton-loading>
        </template>
      </div>
      <div v-if="isExpressShippingSelected" class="cart__item-express-shipping">
        <input type="checkbox" disabled checked />
        <p class="ml-xs my-0">Express Shipping</p>
      </div>
      <div class="cart__item-bundle-wrapper" v-if="bundleAndSaveItem?.html">
        <div v-html="bundleAndSaveItem?.html"></div>
      </div>
      <div class="cart__item-donate-price mt-xs" v-if="donatePrice && item">
        <span>{{ formatPrice(donatePrice * item.quantity) }}</span> will be
        donated to
        <strong>{{ kolCampaignName }}</strong>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { CartLineItem } from '@/types'
import {
  AB_TEST_PRODUCT_TITLE,
  AB_TEST_VARIANT_PRODUCT_CASE_A,
  AB_TEST_VARIANT_PRODUCT_CASE_B,
  AB_TEST_VARIANT_PRODUCT_CASE_C,
  EVENT_TOGGLE_CART_ITEM_PERSONALIZATION,
  HIDDEN_ATTRIBUTE_PREFIX,
  LINE_ITEM_CUSTOMIZATION_URL_ATTRIBUTE,
  LINE_ITEM_PREVIEW_IMAGE_ATTRIBUTE,
  CUSTOM_IMAGE_LINK_PREFIX,
  CUSTOM_AUDIO_LINK_PREFIX,
  CUSTOM_COLOR_PICKER_PREFIX,
  DONATE_PRICE_ATTRIBUTE_KEY,
  KOL_CAMPAIGN_NAME_ATTRIBUTE_KEY,
  PHOTO_ALBUM_ATTRIBUTE_KEY,
  CAMPAIGN_DISCOUNT_BLACKFRIDAY6,
  CAMPAIGN_DISCOUNT_LINE_ITEM_ATTRIBUTE_KEY,
  PRODUCT_TAG_CUSTOMILY_OPTIONSET_ONLY,
  MADEMINE_PRODUCT_HANDLE_PREFIX,
  MADEMINE_PREVIEW_ATTRIBUTE_KEY,
  CUSTOMALL_PRODUCT_HANDLE_PREFIX,
  CUSTOMALL_IMAGE_PREVIEW_ATTRIBUTE_KEY,
  EXPRESS_SHIPPING_ATTRIBUTE_KEY,
  UPSELL_SAME_DESIGN_WITH_PRODUCT_TITLE,
  PRODUCT_TAG_APPLY_FAKE_PRICE_BF9_BUT_NO_DISCOUNT,
  CAMPAIGN_FAKE_PRICE_BF9_BUT_NO_DISCOUNT,
} from '@/utils'
import { computed } from '@vue/reactivity'
import { formatPrice, isProductHasTags } from '@/utils/product'
import ZoominIcon from '@/assets/images/icons/zoom-in.svg'
import TrashIcon from '@/assets/images/icons/trash-icon.svg'
import EditToolPencilIcon from '@/assets/images/icons/edit-tool-pencil.svg'
import Quantity from '@/components/product/Quantity.vue'
import type { Fancybox as FancyboxType } from '@fancyapps/ui'
import EventBus from '@/services/eventbus'
import { ref } from 'vue'
import useSettingStore from '@/store/setting'
import useCartStore from '@/store/cart'
import useRouteStore from '@/store/route'
import { useApplyFakePriceDiscountBF9 } from '@/pages/product/composables/ProductFakePrice'
import useDiscountStore from '@/store/discount'
import IconEditPersionalization from '@/assets/images/icons/edit.png'
import { useRouter } from 'vue-router'
import { useABTest } from '@/composables/useABTest'

interface CartItemProps {
  item: CartLineItem | null
}
const props = withDefaults(defineProps<CartItemProps>(), {
  item: null,
})
const emit = defineEmits<{
  (e: 'link-clicked'): void
}>()

const { getABTestActivedVariantByCode } = useABTest()
const settingStore = useSettingStore()
const cartStore = useCartStore()
const routeStore = useRouteStore()
const discountStore = useDiscountStore()
const { applyFakePriceDiscountBF9 } = useApplyFakePriceDiscountBF9()
const router = useRouter()

let Fancybox: FancyboxType | null = null
const isUseProductImageToPreview = ref(false)
const randomPreviewImageVersion = ref('')
let showPersonalization = false
let isPreviewCustomilyLoading = ref(false)
let timeReLoadPreviewCustomily = ref(0)
let reloadPreivewCustomilyMaxTime = 30
let intervalReloadPreviewCustomily
const isUseProductImageToPreviewCustomily = ref(true)

const productTitleBaseOnABTest = computed(() => {
  if (!props.item?.merchandise?.product) return ''
  const abtestVariant = getABTestActivedVariantByCode(
    AB_TEST_PRODUCT_TITLE
  )
  if (!abtestVariant) return ''
  if (props.item?.merchandise?.product?.handle?.includes('alphabet-initial-for-whiskey-bourbon-lovers-personalized-whisky-whiskey-glass-10470280725scl1cle1ph08th07li09wkg1_ipatuwsk00064')) {
    if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_A) {
      return 'Signature Initial Customizable Glass for Whiskey, Bourbon Lovers'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_B) {
      return 'Golden Letter-Mark Whiskey Decanter Set - Fully Personalized With Heritage Theme'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_C) {
      return 'Gentleman’s Whiskey Ritual Set - Full Item Included Starter Collection'
    }
  } else if (props.item?.merchandise?.product?.handle?.includes('whiskey-cigar-personalized-whisky-wallet-phone-case-10160040825scl1cle1ph08ha09li09lpc1_ipatuwsk00066')) {
    if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_A) {
      return 'Signature Whiskey Wallet Phone Case - Personalized for Him'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_B) {
      return 'Personalized Whiskey Connoisseur Set - Wallet Phone Case Included'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_C) {
      return 'Gentleman’s Whiskey Cigar Ritual Set - Full Item Included Phone Case & Charm'
    }
  } else if (props.item?.merchandise?.product?.handle?.includes('whiskey-and-cigar-personalized-whisky-whiskey-glass-10346220325scl1cle1ph08ha09li09wkg1_ipatuwsk00008')) {
    if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_A) {
      return 'Customizable Glass for Whiskey, Cigar Lovers - Gift for Him'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_B) {
      return 'Signature Personalized Whisky Cigars Decanter Set - For Whiskey & Cigar Lovers'
    } else if (abtestVariant == AB_TEST_VARIANT_PRODUCT_CASE_C) {
      return 'Gentleman’s Whiskey Connoisseur Set - Custom Engraved for Him'
    }
  }
  return ''
})

const fakePriceBF9Applied = computed(() => {
  if (props.item && props.item?.merchandise?.product) {
    const hasFakePriceBF9ButNoDiscountProductTag = isProductHasTags(
      props.item.merchandise.product,
      [PRODUCT_TAG_APPLY_FAKE_PRICE_BF9_BUT_NO_DISCOUNT]
    )
    const campaignFakePriceBF9ButNoDiscount =
      discountStore.campaignDiscountCode ==
      CAMPAIGN_FAKE_PRICE_BF9_BUT_NO_DISCOUNT

    if (
      !hasFakePriceBF9ButNoDiscountProductTag &&
      !campaignFakePriceBF9ButNoDiscount
    ) {
      return
    }

    return applyFakePriceDiscountBF9(
      props.item.merchandise?.product,
      props.item.merchandise?.priceV2?.amount || 0,
      props.item.merchandise?.compareAtPriceV2?.amount || 0,
      props.item.merchandise?.sku
    )
  }
})

const finalPrice = computed(() => {
  if (fakePriceBF9Applied.value) {
    return fakePriceBF9Applied.value
  }

  const lineItemCampaignDiscountAttribute = props.item?.attributes.find(
    (elm) => elm.key == CAMPAIGN_DISCOUNT_LINE_ITEM_ATTRIBUTE_KEY
  )
  const isDiscountBOGO = lineItemCampaignDiscountAttribute?.value == CAMPAIGN_DISCOUNT_BLACKFRIDAY6

  if (isDiscountBOGO && props.item?.priceFromAttribute != null && props.item?.finalPriceFromAttribute != null) {
    return {
      price: props.item?.finalPriceFromAttribute,
      compareAtPrice: props.item?.priceFromAttribute == props.item?.finalPriceFromAttribute ? 0 : props.item?.priceFromAttribute
    }
  }
  
  return {
    price: props.item.price || props.item.merchandise?.priceV2?.amount || 0,
    compareAtPrice:
      props.item.compareAtPrice ||
      props.item.merchandise?.compareAtPriceV2?.amount ||
      0,
  }
})

const isCustomilyOptionsetOnly = computed(() =>
  isProductHasTags(props.item?.merchandise?.product, [
    PRODUCT_TAG_CUSTOMILY_OPTIONSET_ONLY,
  ])
)

const customilyEditPersonalizationLink = computed(() => {
  if (!props.item) return ''
  if (props.item.attributes.length) {
    const customizationAttribute = props.item.attributes.find(
      (elm) => elm.key == LINE_ITEM_CUSTOMIZATION_URL_ATTRIBUTE
    )
    if (customizationAttribute) {
      try {
        const url = new URL(customizationAttribute.value)
        return url.pathname + url.search + url.hash
      } catch (error) {
        return customizationAttribute.value
      }
    }
  }
})

const variantImage = computed(() => {
  if (!props.item) return ''
  return props.item?.merchandise?.image?.url
})

const getPreviewImage = computed(() => {
  if (!props.item) return ''
  if (isMademineProduct.value && preivewImageMademineProduct.value)
    return preivewImageMademineProduct.value
  if (isCustomallProduct.value && preivewImageCustomallProduct.value)
    return preivewImageCustomallProduct.value
  if (isCustomilyOptionsetOnly.value) return variantImage.value
  if (props.item.attributes.length) {
    const previewAttribute = props.item.attributes.find(
      (elm) => elm.key == LINE_ITEM_PREVIEW_IMAGE_ATTRIBUTE
    )
    if (previewAttribute)
      return previewAttribute.value + randomPreviewImageVersion.value
  }
  return variantImage.value
})

const filteredAttributes = computed(() => {
  return (props.item?.attributes || []).filter(
    (elm) => !elm.key.startsWith(HIDDEN_ATTRIBUTE_PREFIX)
  )
})

const isExpressShippingSelected = computed(() => {
  if (!props.item) return
  if (!props.item.attributes.length) return

  const expressShippingAttribute = props.item.attributes.find(
    (elm) => elm.key == EXPRESS_SHIPPING_ATTRIBUTE_KEY
  )
  if (expressShippingAttribute) return expressShippingAttribute.value == 'true'
})

const donatePrice = computed(() => {
  if (!props.item) return
  if (!props.item.attributes.length) return

  const donatePriceAttribute = props.item.attributes.find(
    (elm) => elm.key == DONATE_PRICE_ATTRIBUTE_KEY
  )
  if (donatePriceAttribute) return +donatePriceAttribute.value / 100
})
const kolCampaignName = computed(() => {
  if (!props.item) return
  if (!props.item.attributes.length) return

  const kolCampaignNameAttribute = props.item.attributes.find(
    (elm) => elm.key == KOL_CAMPAIGN_NAME_ATTRIBUTE_KEY
  )
  if (kolCampaignNameAttribute) return kolCampaignNameAttribute.value
})

const bundleAndSaveItem = computed(() => {
  if (
    !settingStore.shop?.bundleAndSave?.length ||
    !cartStore.localCartLineItems.length
  )
    return

  // check item is includes bundle products setting
  const bundleItem = settingStore.shop?.bundleAndSave?.find(
    (item) => item.productHandle == props.item?.merchandise?.product?.handle
  )
  if (!bundleItem) return

  // get quantity bundle product in cart
  let cartBundleProductsQuantity = 0
  const cartBundleProducts = cartStore.localCartLineItems.filter(
    (item) => item?.merchandise?.product?.handle == bundleItem.productHandle
  )
  if (cartBundleProducts.length) {
    cartBundleProducts.forEach((item) => {
      cartBundleProductsQuantity += item.quantity
    })
  }

  // get quantity suggestion product in cart
  let cartSuggestionProductsQuantity = 0
  const cartSuggestionProducts = cartStore.localCartLineItems.filter(
    (item) =>
      item?.merchandise?.product?.handle == bundleItem.productHandleSuggestion
  )
  if (cartSuggestionProducts.length) {
    cartSuggestionProducts.forEach((item) => {
      cartSuggestionProductsQuantity += item.quantity
    })
  }

  if (cartBundleProductsQuantity > cartSuggestionProductsQuantity)
    return bundleItem
})

const isMademineProduct = computed(() =>
  props.item?.merchandise?.product?.handle?.includes(
    MADEMINE_PRODUCT_HANDLE_PREFIX
  )
)

const isCustomallProduct = computed(() =>
  props.item?.merchandise?.product?.handle?.includes(
    CUSTOMALL_PRODUCT_HANDLE_PREFIX
  )
)

const customTextsMademineProduct = computed(() => {
  if (!isMademineProduct.value) return []
  const previewAttribute = props.item?.attributes.find(
    (item) => item.key == MADEMINE_PREVIEW_ATTRIBUTE_KEY
  )
  if (!previewAttribute?.value) return []

  const previewAttributeValue = JSON.parse(previewAttribute.value)

  if (previewAttributeValue?.texts) return previewAttributeValue?.texts || []

  return []
})

const preivewImageMademineProduct = computed(() => {
  if (!isMademineProduct.value) return
  const previewAttribute = props.item?.attributes.find(
    (item) => item.key == MADEMINE_PREVIEW_ATTRIBUTE_KEY
  )
  if (!previewAttribute?.value) return

  const previewAttributeValue = JSON.parse(previewAttribute.value)

  if (previewAttributeValue?.images?.length)
    return previewAttributeValue?.images[0]
})

const preivewImageCustomallProduct = computed(() => {
  if (!isCustomallProduct.value) return
  const imagePreviewAttribute = props.item?.attributes.find(
    (item) => item.key == CUSTOMALL_IMAGE_PREVIEW_ATTRIBUTE_KEY
  )
  if (!imagePreviewAttribute) return

  return imagePreviewAttribute.value
})

const getUpsellSameDesignProductTitleWith = computed(() => {
  if (!props.item?.attributes?.length) return ''
  const upsellSameDesignProductTitleAttribute = props.item.attributes.find(
    (elm) => elm.key == UPSELL_SAME_DESIGN_WITH_PRODUCT_TITLE
  )
  if (upsellSameDesignProductTitleAttribute)
    return upsellSameDesignProductTitleAttribute.value
})

async function previewImage() {
  if (!Fancybox) {
    Fancybox = (await import('@fancyapps/ui')).Fancybox
  }
  // add a random version to preview image to force cache
  if (isUseProductImageToPreview.value) {
    randomPreviewImageVersion.value = `?&v=${Math.floor(Math.random() * 1000)}`
  }
  if (getPreviewImage.value) {
    const photoAlbum = getCustomPhotoAlbum()
    if (photoAlbum.length) {
      Fancybox.show(
        photoAlbum.map((image) => ({
          src: image,
          type: 'image',
        }))
      )
    } else {
      Fancybox.show([
        {
          src: getPreviewImage.value,
          type: 'image',
        },
      ])
    }
  }

  // Analytics.track(
  //   EVENT_TRACKING_CLICK_PREVIEW_ON_CART,
  //   {},
  //   destinationIntegrations([MIXPANEL_DESTINATION])
  // )
}

async function previewCustomImage(attributeKey: string) {
  if (!props.item?.attributes?.length || !attributeKey) return
  const customLink = getCustomLink(attributeKey)
  const customKey = getKeyCustomAttribute(customLink)
  if (!Fancybox) {
    Fancybox = (await import('@fancyapps/ui')).Fancybox
  }
  if (customLink) {
    Fancybox.show([
      {
        src: customLink,
        type: customKey.includes(CUSTOM_IMAGE_LINK_PREFIX) ? 'image' : 'iframe',
      },
    ])
  }
}

function getCustomLink(attributeKey: string) {
  if (!props.item?.attributes?.length || !attributeKey) return ''
  let index = props.item?.attributes.findIndex((item) =>
    item.key.includes(`${CUSTOM_IMAGE_LINK_PREFIX} ${attributeKey}`)
  )
  if (index === -1) {
    index = props.item?.attributes.findIndex((item) =>
      item.key.includes(`${CUSTOM_AUDIO_LINK_PREFIX} ${attributeKey}`)
    )
  }
  if (index === -1) return ''
  return props.item?.attributes[index].value
}

function getCustomColor(attributeKey: string) {
  if (!props.item?.attributes?.length || !attributeKey) return ''
  let index = props.item?.attributes.findIndex((item) =>
    item.key.includes(`${CUSTOM_COLOR_PICKER_PREFIX} ${attributeKey}`)
  )
  if (index === -1) return ''
  return props.item?.attributes[index].value
}

function getCustomPhotoAlbum() {
  if (!props.item?.attributes?.length) return []
  let index = props.item?.attributes.findIndex((item) =>
    item.key.includes(PHOTO_ALBUM_ATTRIBUTE_KEY)
  )
  if (index === -1) return []

  return props.item?.attributes[index]?.value?.split(',') || []
}

function getKeyCustomAttribute(value: string) {
  if (!props.item?.attributes?.length || !value) return ''
  let index = props.item?.attributes.findIndex((item) => item.value === value)
  if (index === -1) return ''
  return props.item?.attributes[index].key
}

function hasCustomImageLink(attributeKey: string) {
  return !!getCustomLink(attributeKey)
}
function hasCustomColor(attributeKey: string) {
  return !!getCustomColor(attributeKey)
}

function onTogglePersonalization() {
  showPersonalization = !showPersonalization

  EventBus.trigger(EVENT_TOGGLE_CART_ITEM_PERSONALIZATION)
}

function onPreviewImageError() {
  isUseProductImageToPreviewCustomily.value = false
  isUseProductImageToPreview.value = true
  isPreviewCustomilyLoading.value = true
  const threeSecondInMiliseconds = 3000
  async function reFetchPreviewImageCustomily() {
    const response = await fetch(currentURL, { cache: 'reload' })
    if (
      response?.ok ||
      timeReLoadPreviewCustomily.value == reloadPreivewCustomilyMaxTime
    ) {
      isPreviewCustomilyLoading.value = false
      isUseProductImageToPreviewCustomily.value = true
      clearTimeout(intervalReloadPreviewCustomily)
    }
  }

  const currentURL = getPreviewImage.value
  if (
    currentURL &&
    timeReLoadPreviewCustomily.value < reloadPreivewCustomilyMaxTime
  ) {
    try {
      intervalReloadPreviewCustomily = setInterval(
        reFetchPreviewImageCustomily,
        threeSecondInMiliseconds
      )
    } catch (error) {
      isUseProductImageToPreview.value = true
      isPreviewCustomilyLoading.value = false
    }
  } else {
    isUseProductImageToPreview.value = true
    isPreviewCustomilyLoading.value = false
  }

  timeReLoadPreviewCustomily.value += 1
}

function onClickEditPersonalization() {
  if (!customilyEditPersonalizationLink.value) return
  router.push(customilyEditPersonalizationLink.value)
  emit('link-clicked')
}

function buildProductHandle(productPath: string) {
  // if product mademine we need ssr to redirect to customize domain
  const isMademineProduct = productPath.includes(MADEMINE_PRODUCT_HANDLE_PREFIX)
  const isCustomallProduct = productPath.includes(
    CUSTOMALL_PRODUCT_HANDLE_PREFIX
  )
  if (isMademineProduct || isCustomallProduct) {
    return `${routeStore.currentOrigin}${productPath}`
  }

  return productPath
}
</script>

<style lang="scss">
.cart {
  $S: &;
  &__item {
    display: grid;
    grid-template-columns: 120px minmax(0, 1fr);
    grid-template-rows: max-content 1fr;
    gap: 0 1em;
    grid-template-areas:
      'a b'
      'c b';
    margin-bottom: 2em;

    &:hover {
      #{$S}__item-title a {
        color: var(--color-primary);
      }
    }

    &-title a {
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      max-height: 4.5em;
      overflow: hidden;
      color: var(--ink);
      transition: all var(--animation-duration);
    }

    &-image {
      grid-area: a;
      min-height: 120px;
      position: relative;
    }

    .product-preview-customily-loading {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      --loading-size: 16px;
      --loading-line-width: 2px;
      color: #555555;
    }
    .overlay {
      opacity: 0.5;
    }

    &-image-preview {
      grid-area: c;
      align-self: start;
      font-size: 12px;
      font-weight: normal;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 0.3em;
      color: var(--coral-base);
      transition: color var(--animation-duration) ease;

      svg {
        width: 1em;
        height: 1em;

        path {
          stroke: currentColor !important;
          transition: stroke var(--animation-duration) ease;
        }
      }

      &:hover {
        color: var(--coral-dark);
      }
    }

    &-edit-persionalization {
      transform: translateY(2px);
      cursor: pointer;
    }

    &-same-design {
      font-size: 12px;
      font-weight: 500;
    }

    $cartItem: &;

    &-content {
      grid-area: b;
      display: flex;
      flex-direction: column;
      gap: 0.3em;

      > #{$cartItem}-image-preview {
        display: none;
      }
    }

    &-price {
      display: flex;
      flex-wrap: wrap;
      gap: 4px;
    }

    &-price-final {
      color: var(--color-secondary);
    }

    &-price-compare {
      text-decoration: line-through;
      color: var(--text-color-tertiary);
    }

    &-options {
      font-size: 12px;
      color: var(--text-color-secondary);
    }

    &-attributes {
      padding: 1em 0;
      border: dashed 1px var(--border-color);
      border-left: none;
      border-right: none;

      summary {
        font-size: 12px;
        font-weight: bold;
        cursor: pointer;
      }

      .font-weight-normal {
        font-weight: normal;
      }
    }

    &-attribute-list {
      margin-top: 1em;
      font-size: 12px;
      display: grid;
      grid-template-columns: 40% minmax(0, 1fr);
      gap: 0.5em;
      word-break: break-all;
    }

    &-attribute-title {
      color: var(--text-color-tertiary);
    }

    &-quantity-container {
      display: flex;
      align-items: center;
      gap: 1em;
    }

    .item__quantity {
      height: 2.25rem;
    }

    &-delete-button {
      cursor: pointer;
      display: flex;
      justify-content: center;

      svg {
        height: 20px;
      }
    }

    &-donate-price {
      border-top: dashed 1px var(--border-color);
      padding-top: 1em;
      span {
        color: var(--color-secondary);
      }
    }
  }

  @include media-desktop {
    &--full-page {
      #{$S} {
        &__item {
          grid-template-columns: 135px minmax(0, 1fr);
          gap: 1.5em;

          &-image-preview {
            display: none;
          }

          &-content {
            display: grid;
            grid-template-columns: 5fr 3fr;
            grid-template-areas:
              'a b'
              'c b'
              'd b'
              'e b'
              'f b';

            & #{$S}__item-image-preview {
              display: flex;
              justify-content: flex-start;
            }
          }

          &-title {
            grid-area: a;
          }

          &-price {
            grid-area: c;
          }

          &-options {
            grid-area: d;
          }

          &-image-preview {
            grid-area: e;
          }

          &-attributes {
            grid-area: f;
          }

          &-quantity-container {
            grid-area: b;
            justify-content: space-evenly;
          }
        }
      }
    }
  }
  .custom-color-picker {
    position: relative;
    top: 2px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid #b3b3b3;
    display: inline-block;
    margin-right: 4px;
  }
  .cart__item-express-shipping {
    display: flex;
    align-items: center;
    font-size: 12px;
    font-weight: 600;
  }
  .cart__item-bundle-wrapper {
    font-size: 12px;
    p {
      margin: 4px 0;
    }
  }
  .cart__item-bundle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 500;
    img {
      max-width: 60px;
      width: 100%;
      aspect-ratio: 1;
      object-fit: cover;
      border: 1px solid var(--border-color);
    }
  }
}

.cart__item-title {
  color: var(--ink);
}
</style>
